// Combat gear v2 (PLR-3; M17). PURE (ENG-1): armour defence + enchant protection
// (EPF), the shield block arc, the mace fall-smash curve, and the totem of undying
// death-cancel. No host / three / DOM / audio — the host adapts equipped items in
// and applies the returned decisions. Follows Minecraft to documented tolerance
// (D12): the armour/toughness curve, the protection-type EPF weights, the ±90°
// shield arc, and the mace tier curve are faithful; effect magnitudes are ours.
import { COMBAT } from '../config';
import { mitigate } from './combat';
import { ELYTRA_ITEM } from './elytra';
import type { ItemStack } from './inventory';

// ── Armour material stats ─────────────────────────────────────────────────────
export interface ArmorStats {
  /** Defence points (feed the PLR-3 armour/toughness curve). */
  points: number;
  /** Toughness — softens high-damage hits (diamond 2/pc, netherite 3/pc). */
  toughness: number;
  /** Knockback-resistance fraction this piece contributes (netherite 0.1/pc). */
  knockback: number;
}

/** Slot index an armour piece occupies (helmet=0 … boots=3), matching INV.ARMOR. */
export type GearSlot = 0 | 1 | 2 | 3;

interface ArmorMaterial {
  /** Item-name prefix, e.g. `diamond` → `diamond_helmet`. */
  prefix: string;
  /** Defence points per slot `[helmet, chest, legs, boots]`. */
  points: readonly [number, number, number, number];
  toughness: number;
  knockback: number;
  /** Enchantability (PLR-6 table reach) — armour has its own values, distinct
   *  from the tool table (leather 15 · chain 12 · iron 9 · gold 25 · diamond 10). */
  enchant: number;
}

/** §13 armour material matrix. Points per slot are Minecraft-faithful; toughness
 *  and knockback-resistance are the diamond/netherite bonuses. */
export const ARMOR_MATERIALS: readonly ArmorMaterial[] = [
  { prefix: 'leather', points: [1, 3, 2, 1], toughness: 0, knockback: 0, enchant: 15 },
  { prefix: 'chainmail', points: [2, 5, 4, 1], toughness: 0, knockback: 0, enchant: 12 },
  { prefix: 'iron', points: [2, 6, 5, 2], toughness: 0, knockback: 0, enchant: 9 },
  { prefix: 'golden', points: [2, 5, 3, 1], toughness: 0, knockback: 0, enchant: 25 },
  { prefix: 'diamond', points: [3, 8, 6, 3], toughness: 2, knockback: 0, enchant: 10 },
  { prefix: 'netherite', points: [3, 8, 6, 3], toughness: 3, knockback: 0.1, enchant: 15 },
] as const;

/** Slot-suffix ↔ index. The suffix doubles as the enchant category (helmet /
 *  chestplate / leggings / boots) so the table can offer armour enchants. */
const SLOT_SUFFIX: readonly [string, GearSlot][] = [
  ['helmet', 0], ['chestplate', 1], ['leggings', 2], ['boots', 3],
];

interface ArmorEntry { slot: GearSlot; category: string; enchant: number; stats: ArmorStats }

/** Build the item-name → armour-entry table from the material matrix (plus the
 *  turtle helmet). Exported so the inventory slot filter, the mitigation path,
 *  and the enchant table all read one source of truth. */
export function buildArmorTable(): Map<string, ArmorEntry> {
  const table = new Map<string, ArmorEntry>();
  for (const m of ARMOR_MATERIALS) {
    for (const [suffix, slot] of SLOT_SUFFIX) {
      table.set(`${m.prefix}_${suffix}`, {
        slot,
        category: suffix,
        enchant: m.enchant,
        stats: { points: m.points[slot], toughness: m.toughness, knockback: m.knockback },
      });
    }
  }
  // Turtle helmet: an iron-tier helmet (its water-breathing bonus is world-side).
  table.set('turtle_helmet', { slot: 0, category: 'helmet', enchant: 9, stats: { points: 2, toughness: 0, knockback: 0 } });
  return table;
}

const ARMOR_TABLE = buildArmorTable();

/** The armour slot an item fits (helmet=0 … boots=3), or null if it isn't armour.
 *  This is the `armorSlotOf` the inventory + player use. Elytra occupy the chest
 *  slot too (they grant glide, not defence — see game/elytra.ts). */
export function armorSlotOf(itemName: string | undefined): GearSlot | null {
  if (itemName === ELYTRA_ITEM) return 1; // chest slot, no defence/enchant stats
  return itemName === undefined ? null : (ARMOR_TABLE.get(itemName)?.slot ?? null);
}

/** Defence stats of an armour item, or null if it isn't armour. */
export function armorStatsOf(itemName: string | undefined): ArmorStats | null {
  return itemName === undefined ? null : (ARMOR_TABLE.get(itemName)?.stats ?? null);
}

/** Enchant category of an armour item (its slot: helmet/chestplate/leggings/
 *  boots), or null if it isn't armour — feeds the enchanting table (PLR-6). */
export function armorCategory(itemName: string | undefined): string | null {
  return itemName === undefined ? null : (ARMOR_TABLE.get(itemName)?.category ?? null);
}

/** Enchantability of an armour item (table reach), or null if it isn't armour. */
export function armorEnchantability(itemName: string | undefined): number | null {
  return itemName === undefined ? null : (ARMOR_TABLE.get(itemName)?.enchant ?? null);
}

/** Sum equipped armour into total points / toughness / knockback-resistance. */
export function armorTotals(stats: ReadonlyArray<ArmorStats | null | undefined>): ArmorStats {
  let points = 0, toughness = 0, knockback = 0;
  for (const s of stats) {
    if (!s) continue;
    points += s.points;
    toughness += s.toughness;
    knockback += s.knockback;
  }
  return { points, toughness, knockback: Math.min(1, knockback) };
}

// ── Enchant protection (EPF) ─────────────────────────────────────────────────
/** Damage class that selects which protection enchants apply. */
export type DamageType = 'generic' | 'projectile' | 'fire' | 'blast' | 'fall';

/** EPF one armour piece's enchants contribute against `type` (Protection applies
 *  to every type; the specialised protections add their per-level bonus to just
 *  their own type; Feather Falling only cushions falls). */
export function pieceEPF(enchants: ReadonlyArray<{ id: string; lvl: number }> | undefined, type: DamageType): number {
  if (!enchants) return 0;
  let epf = 0;
  for (const { id, lvl } of enchants) {
    if (id === 'protection') epf += COMBAT.EPF.PROTECTION_ALL * lvl;
    else if (id === 'fire_protection' && type === 'fire') epf += COMBAT.EPF.FIRE_PROTECTION * lvl;
    else if (id === 'blast_protection' && type === 'blast') epf += COMBAT.EPF.BLAST_PROTECTION * lvl;
    else if (id === 'projectile_protection' && type === 'projectile') epf += COMBAT.EPF.PROJECTILE_PROTECTION * lvl;
    else if (id === 'feather_falling' && type === 'fall') epf += COMBAT.EPF.FEATHER_FALLING * lvl;
  }
  return epf;
}

/** Total EPF across the four equipped armour pieces for a damage type (uncapped;
 *  `mitigate` applies the ×0.04-per-point cap at COMBAT.EPF_CAP). */
export function protectionEPF(
  pieces: ReadonlyArray<ReadonlyArray<{ id: string; lvl: number }> | undefined>,
  type: DamageType,
): number {
  let epf = 0;
  for (const p of pieces) epf += pieceEPF(p, type);
  return epf;
}

/** One-shot PLR-3 mitigation from equipped gear: composes the armour/toughness
 *  curve with the damage-type EPF term (this is the completion table). */
export function absorbWithGear(
  base: number,
  totals: ArmorStats,
  pieces: ReadonlyArray<ReadonlyArray<{ id: string; lvl: number }> | undefined>,
  type: DamageType,
): number {
  return mitigate(base, totals.points, totals.toughness, protectionEPF(pieces, type));
}

// ── Shield ───────────────────────────────────────────────────────────────────
export interface ShieldHit {
  /** Facing (horizontal unit look vector) of the shield holder. */
  facX: number;
  facZ: number;
  /** World position of the damage source (attacker / projectile / explosion). */
  srcX: number;
  srcZ: number;
  /** World position of the shield holder. */
  selfX: number;
  selfZ: number;
  /** The shield is currently disabled (recent axe hit). */
  disabled?: boolean;
}

/** Whether a raised shield blocks the hit: the source must lie within ±90° of
 *  facing (frontal 180° arc, COMBAT.SHIELD_ARC_DEG) and the shield not disabled.
 *  Melee/projectile blocked → 0 damage; the host handles the axe-disable timer. */
export function shieldBlocks(hit: ShieldHit): boolean {
  if (hit.disabled) return false;
  const dx = hit.srcX - hit.selfX;
  const dz = hit.srcZ - hit.selfZ;
  const len = Math.hypot(dx, dz);
  if (len === 0) return false; // source on top of us — no front to face
  // dot(facing, dir-to-source) > 0 ⇒ source within the frontal hemisphere.
  const cosThreshold = Math.cos((COMBAT.SHIELD_ARC_DEG * Math.PI) / 180); // 90° → 0
  return (hit.facX * dx + hit.facZ * dz) / len > cosThreshold;
}

// ── Mace ─────────────────────────────────────────────────────────────────────
/** Extra smash damage from a mace hit after falling `fallDistance` blocks: a
 *  three-tier curve (+4/blk first 3, +2/blk next 5, +1/blk beyond). 0 unless
 *  falling. Wind-charge synergy (bouncing to build fall distance) is world-side. */
export function maceBonusDamage(fallDistance: number): number {
  if (fallDistance <= 0) return 0;
  const { HIGH, HIGH_UNTIL, MID, MID_SPAN, LOW } = COMBAT.MACE;
  const high = Math.min(fallDistance, HIGH_UNTIL);
  const mid = Math.min(Math.max(fallDistance - HIGH_UNTIL, 0), MID_SPAN);
  const low = Math.max(fallDistance - HIGH_UNTIL - MID_SPAN, 0);
  return HIGH * high + MID * mid + LOW * low;
}

/** Total mace damage: the weapon's base plus the fall-smash bonus (PLR-3). */
export function maceDamage(base: number, fallDistance: number): number {
  return base + maceBonusDamage(fallDistance);
}

// ── Totem of undying ─────────────────────────────────────────────────────────
export interface TotemEffect { id: string; amplifier: number; ticks: number }
export interface TotemResult {
  /** The totem fired — the host must have consumed one from hand/offhand. */
  activated: boolean;
  /** Health to force the reviving entity to (0 when it dies for real). */
  newHealth: number;
  /** Effects to grant on revival. */
  effects: readonly TotemEffect[];
}

/** Resolve a lethal hit against a held totem (PLR-3). If the post-damage health
 *  would be ≤ 0 and a totem is available, cancel death: revive to REVIVE_HP with
 *  the totem effects. Otherwise the hit stands. The host consumes the totem item
 *  and applies the effects; this is the pure decision. */
export function resolveTotem(postDamageHealth: number, hasTotem: boolean): TotemResult {
  if (postDamageHealth > 0 || !hasTotem) {
    return { activated: false, newHealth: Math.max(0, postDamageHealth), effects: [] };
  }
  return {
    activated: true,
    newHealth: COMBAT.TOTEM.REVIVE_HP,
    effects: COMBAT.TOTEM.EFFECTS.map((e) => ({ ...e })),
  };
}

/** Read the enchant list off an armour stack (for `protectionEPF`). */
export function stackEnchants(stack: ItemStack | null | undefined): ReadonlyArray<{ id: string; lvl: number }> | undefined {
  return stack?.components?.enchants;
}
